<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">External ID</vx-label>

                    <vx-input
                        data-test="group-id-input"
                        autofocus
                        v-model="form.group_id"
                        size="lg"
                        placeholder="Enter external ID"
                        :error="displayFirstError(validate.group_id.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Name</vx-label>

                    <vx-input
                        data-test="name-input"
                        v-model="form.name"
                        size="lg"
                        placeholder="Enter group name"
                        :error="displayFirstError(validate.name.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Time-zone</vx-label>

                    <vx-selectable
                        v-model="form.time_zone"
                        :options="timeZonesList"
                        size="lg"
                        :reduce="(option) => option.value"
                        placeholder="Select a time-zone"
                        :clearable="true"
                        :error="displayFirstError(validate.time_zone.$errors)"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">Team</vx-label>

                    <vx-selectable
                        v-model="form.team_id"
                        :options="teams"
                        @search="search"
                        size="lg"
                        label="name"
                        :reduce="(option) => option.id"
                        :clearable="false"
                        placeholder="Select a team"
                        :error="displayFirstError(validate.team_id.$errors)"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">HubSpot Company ID </vx-label>

                    <hubspot-company-id-input
                        v-model="form.hubspot_company_id"
                        placeholder="HubSpot Company ID"
                        size="lg"
                        :error="displayFirstError($externalResults.hubspot_company_id)"
                    />
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import HubspotCompanyIdInput from '~/components/general/HubspotCompanyIdInput.vue';
import { ref, computed } from 'vue';
import { adminTeamsClient } from '~/services';
import timeZones from '~/utils/timeZones';
import { displayFirstError, groupAndOrgUnitName } from '~/utils/validation';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import debounce from '~/utils/debounce';
import { maxLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

const { $externalResults } = useFormRequestHandler();

const form = defineModel({
    required: true,
    type: Object,
});

const teams = ref([]);

const search = debounce((query) => {
    adminTeamsClient
        .listTeams({
            query,
            status: 'active',
            has_feature: 'contact_groups',
        })
        .then(
            (res) =>
                (teams.value = res.data.data.map((team) => ({
                    ...team,
                    name: `${team.id} - ${team.name}`,
                })))
        );
});

search();

const timeZonesList = computed(() =>
    timeZones.map((timeZone) => ({
        value: timeZone,
        label: timeZone.split('_').join(' '),
    }))
);

const validate = useVuelidate(
    {
        group_id: {
            required,
            maxLength: maxLength(255),
        },
        name: {
            required,
            maxLength: maxLength(80),
            format: groupAndOrgUnitName,
        },
        team_id: {
            required,
        },
        hubspot_company_id: {},
        time_zone: {},
    },
    form
);
</script>
